
// src/components/admin/page-management/section-forms/ProductCatalogForm.js
import React, { useEffect, useState } from 'react';
import { Info ,Search,Sliders,Check,ShoppingCart,Zap,DollarSign,Slider,Sparkles,Layout,Type} from 'lucide-react';
import axios from 'axios';

const ProductCatalogForm = ({ content, updateContent }) => {
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // useEffect to fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      if (content?.showAllCategories === false) {
        try {
          setLoadingCategories(true);
          const response = await axios.get('/api/categories/basic');
          setAvailableCategories(response.data);
          
          // Initialize selected categories if not already set
          if (!content.selectedCategoryIds) {
            const initialSelected = response.data.map(cat => cat._id);
            updateContent('selectedCategoryIds', initialSelected);
          }
        } catch (error) {
          console.error('Error fetching categories:', error);
        } finally {
          setLoadingCategories(false);
        }
      }
    };
    
    fetchCategories();
  }, [content?.showAllCategories]);

  // useEffect to update the category selection UI
  useEffect(() => {
    const container = document.getElementById('category-selection-container');
    if (container && availableCategories.length > 0 && content?.showAllCategories === false) {
      container.innerHTML = '';
      
      const div = document.createElement('div');
      div.className = 'max-h-60 overflow-y-auto border rounded p-2';
      
      availableCategories.forEach(category => {
        const isSelected = content.selectedCategoryIds?.includes(category._id) || false;
        
        const checkboxDiv = document.createElement('div');
        checkboxDiv.className = 'flex items-center mb-2';
        
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'mr-2';
        input.checked = isSelected;
        input.onchange = () => {
          const currentSelected = [...(content.selectedCategoryIds || [])];
          if (input.checked && !currentSelected.includes(category._id)) {
            updateContent('selectedCategoryIds', [...currentSelected, category._id]);
          } else if (!input.checked && currentSelected.includes(category._id)) {
            updateContent('selectedCategoryIds', currentSelected.filter(id => id !== category._id));
          }
        };
        
        const label = document.createElement('label');
        label.className = 'text-sm';
        label.textContent = category.name;
        
        checkboxDiv.appendChild(input);
        checkboxDiv.appendChild(label);
        div.appendChild(checkboxDiv);
      });
      
      container.innerHTML = '';
      container.appendChild(div);
    }
  }, [availableCategories, content?.selectedCategoryIds, content?.showAllCategories]);

  const handleColorChange = (key, value) => {
    // Ensure color values include the # if missing
    if (value && !value.startsWith('#') && value.length > 0) {
      value = '#' + value;
    }
    updateContent(key, value);
  };
  
 // Mock state for demonstration
 const [setContent] = useState({
  productDisplayFields: {
    showName: true,
    showPrice: true,
    showDescription: false,
    showNote: false,
    showRating: false,
    productsPerRow: 3,
    productCardStyle: 'shadow',
    showQuickAction: true,
    quickActionIcon: 'customize'
  },
  productInfoPosition: 'below',
  productTextAlignment: 'left',
  productNameLines: 2,
  productDescriptionLines: 3,
  productImageAspectRatio: 'square',
  productClickBehavior: 'navigate',
  productPopupSettings: {
    showGallery: true,
    showDescription: true,
    showPrice: true,
    showNote: true,
    showAttributes: true,
    showAddToCart: true,
    showCustomizeButton: true,
    popupSize: 'medium'
  },
  categoryApiPath: '/api/categories',
    productApiPath: '/api/products/basic',
});
const [isAdvancedMode, setIsAdvancedMode] = useState(false);
const [showTooltip, setShowTooltip] = useState('');
const resetToDefaults = () => {
  setContent({
    categoryApiPath: '/api/categories',
    productApiPath: '/api/products/basic'
  });
};

const validateApiPath = (path) => {
  if (!path) return { isValid: false, message: 'Path cannot be empty' };
  if (!path.startsWith('/')) return { isValid: false, message: 'Path must start with /' };
  if (path.includes(' ')) return { isValid: false, message: 'Path cannot contain spaces' };
  return { isValid: true, message: '' };
};

const categoryValidation = validateApiPath(content?.categoryApiPath);
const productValidation = validateApiPath(content?.productApiPath);

// const updateContent = (key, value) => {
//   setContent(prev => ({
//     ...prev,
//     [key]: value
//   }));
// };


const [activeSection, setActiveSection] = useState('display');

  return (
    <div>
      <h3 className="font-bold text-lg mb-4">Product Catalog Section</h3>
      
    
      {/* Enhanced Section Header */}
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-blue-100 p-2 rounded-lg mr-3">
      <Type className="h-5 w-5 text-blue-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Section Header</h4>
      <p className="text-sm text-gray-500 mt-1">Configure the main title and description for your section</p>
    </div>
  </div>
  
  <div className="space-y-6">
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Section Title</label>
      <input
        type="text"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        value={content?.title || 'Products'}
        onChange={(e) => updateContent('title', e.target.value)}
        placeholder="Our Products"
      />
      <p className="text-xs text-gray-500">Main heading displayed above the section content</p>
    </div>
    
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Section Description</label>
      <textarea
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
        rows="3"
        value={content?.description || 'Browse our selection of premium packaging solutions'}
        onChange={(e) => updateContent('description', e.target.value)}
        placeholder="Brief description of your section content"
      />
      <p className="text-xs text-gray-500">Supporting text that appears below the section title</p>
    </div>
    
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Background Color</label>
      <div className="flex items-center space-x-3">
        <input
          type="color"
          className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
          value={content?.backgroundColor || '#F9FAFB'}
          onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
        />
        <div 
          className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
          style={{ backgroundColor: content?.backgroundColor || '#F9FAFB' }}
        />
      </div>
      <p className="text-xs text-gray-500">Background color for the entire section</p>
    </div>
    
    {/* Live Preview */}
    <div className="border-t border-gray-200 pt-6">
      <h6 className="font-medium text-gray-900 mb-3">Preview</h6>
      <div 
        className="border border-gray-200 rounded-lg p-6 text-center"
        style={{ backgroundColor: content?.backgroundColor || '#F9FAFB' }}
      >
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          {content?.title || 'Products'}
        </h3>
        <p className="text-gray-600">
          {content?.description || 'Browse our selection of premium packaging solutions'}
        </p>
      </div>
    </div>
  </div>
</div>
      
{/* Enhanced Search Configuration */}
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-green-100 p-2 rounded-lg mr-3">
      <Search className="h-5 w-5 text-green-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Search Configuration</h4>
      <p className="text-sm text-gray-500 mt-1">Enable and customize the search functionality for users</p>
    </div>
  </div>
  
  <div className="space-y-6">
    {/* Enable Search Toggle */}
    <div className="flex items-start space-x-3">
      <div className="flex items-center h-5">
        <input
          type="checkbox"
          id="showSearch"
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          checked={content?.showSearch !== false}
          onChange={(e) => updateContent('showSearch', e.target.checked)}
        />
      </div>
      <div className="flex-1">
        <label htmlFor="showSearch" className="text-sm font-medium text-gray-700">Enable Search Functionality</label>
        <p className="text-xs text-gray-500 mt-1">Allow users to search through content by keywords</p>
      </div>
    </div>
    
    {/* Search Settings */}
    {content?.showSearch !== false && (
      <div className="border-t border-gray-200 pt-6 space-y-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Search Placeholder Text</label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={content?.searchPlaceholder || 'Search between all categories...'}
            onChange={(e) => updateContent('searchPlaceholder', e.target.value)}
            placeholder="Search products..."
          />
          <p className="text-xs text-gray-500">Text shown in the search input field when empty</p>
        </div>
        
        {/* Search Preview */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h6 className="font-medium text-gray-900 mb-3">Search Bar Preview</h6>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-500 cursor-not-allowed"
              placeholder={content?.searchPlaceholder || 'Search between all categories...'}
              disabled
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">This is how the search bar will appear to users</p>
        </div>
        
        {/* Search Features Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                <Info className="h-3 w-3 text-white" />
              </div>
            </div>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Search Features</p>
              <ul className="space-y-1 text-xs">
                <li>• Real-time search as users type</li>
                <li>• Searches through product names and descriptions</li>
                <li>• Case-insensitive matching</li>
                <li>• Automatic filtering of results</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    )}
    
    {/* Disabled State Info */}
    {content?.showSearch === false && (
      <div className="border-t border-gray-200 pt-6">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                <Search className="h-4 w-4 text-gray-500" />
              </div>
            </div>
            <div>
              <h6 className="font-medium text-gray-700">Search Disabled</h6>
              <p className="text-sm text-gray-500">Users will not see a search bar. Enable search above to allow content filtering.</p>
            </div>
          </div>
        </div>
      </div>
    )}
    
    {/* Configuration Summary */}
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <h6 className="font-medium text-gray-900">Search Status</h6>
          <p className="text-sm text-gray-500">Current search configuration</p>
        </div>
        <div className="text-right">
          <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            content?.showSearch !== false 
              ? 'bg-green-100 text-green-800' 
              : 'bg-gray-100 text-gray-800'
          }`}>
            {content?.showSearch !== false ? 'Enabled' : 'Disabled'}
          </div>
          {content?.showSearch !== false && (
            <div className="text-xs text-gray-500 mt-1">
              Placeholder: "{content?.searchPlaceholder || 'Search between all categories...'}"
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
</div>

{/* Enhanced Category Slider Configuration */}
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-purple-100 p-2 rounded-lg mr-3">
      <Sliders className="h-5 w-5 text-purple-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Category Navigation</h4>
      <p className="text-sm text-gray-500 mt-1">Configure the category filtering interface and appearance</p>
    </div>
  </div>
  
  <div className="space-y-6">
    {/* Enable Category Slider Toggle */}
    <div className="flex items-start space-x-3">
      <div className="flex items-center h-5">
        <input
          type="checkbox"
          id="showCategorySlider"
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          checked={content?.showCategorySlider !== false}
          onChange={(e) => updateContent('showCategorySlider', e.target.checked)}
        />
      </div>
      <div className="flex-1">
        <label htmlFor="showCategorySlider" className="text-sm font-medium text-gray-700">Show Category Navigation</label>
        <p className="text-xs text-gray-500 mt-1">Display category filter buttons above the content</p>
      </div>
    </div>
    
    {/* Category Slider Settings */}
    {content?.showCategorySlider !== false && (
      <div className="border-t border-gray-200 pt-6 space-y-6">
        {/* Size Configuration */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Category Item Size</label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={content?.categoryItemSize || 'medium'}
            onChange={(e) => updateContent('categoryItemSize', e.target.value)}
          >
            <option value="small">Small - Compact category buttons</option>
            <option value="medium">Medium - Standard size (recommended)</option>
            <option value="large">Large - Prominent category display</option>
          </select>
          <p className="text-xs text-gray-500">Size of individual category navigation items</p>
        </div>
        
        {/* Color Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Default Border Color</label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                value={content?.categoryBorderColor || '#3B82F6'}
                onChange={(e) => handleColorChange('categoryBorderColor', e.target.value)}
              />
              <div 
                className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                style={{ backgroundColor: content?.categoryBorderColor || '#3B82F6' }}
              />
              <span className="text-sm text-gray-600">{content?.categoryBorderColor || '#3B82F6'}</span>
            </div>
            <p className="text-xs text-gray-500">Border color for unselected category items</p>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Selected Border Color</label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                value={content?.selectedCategoryBorderColor || '#F59E0B'}
                onChange={(e) => handleColorChange('selectedCategoryBorderColor', e.target.value)}
              />
              <div 
                className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                style={{ backgroundColor: content?.selectedCategoryBorderColor || '#F59E0B' }}
              />
              <span className="text-sm text-gray-600">{content?.selectedCategoryBorderColor || '#F59E0B'}</span>
            </div>
            <p className="text-xs text-gray-500">Border color for the currently active category</p>
          </div>
        </div>
        
        {/* Initial Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Initial Selected Category</label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={content?.initialCategorySlug || ''}
            onChange={(e) => updateContent('initialCategorySlug', e.target.value)}
            placeholder="shopping-bags"
          />
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <span className="font-medium">Optional:</span> Enter a category slug to pre-select when the page loads. 
              If left empty, the system will use the URL parameter or show no initial selection.
            </p>
          </div>
        </div>
        
        {/* Category Preview */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h6 className="font-medium text-gray-900 mb-3">Category Navigation Preview</h6>
          <div className="flex space-x-3 overflow-x-auto pb-2">
            {/* Sample Category Items */}
            <div 
              className={`flex-shrink-0 px-4 py-2 rounded-lg border-2 text-sm font-medium cursor-pointer transition-colors ${
                content?.categoryItemSize === 'small' ? 'text-xs px-3 py-1' :
                content?.categoryItemSize === 'large' ? 'text-base px-5 py-3' : 'text-sm px-4 py-2'
              }`}
              style={{ 
                borderColor: content?.selectedCategoryBorderColor || '#F59E0B',
                backgroundColor: (content?.selectedCategoryBorderColor || '#F59E0B') + '10'
              }}
            >
              Selected Category
            </div>
            <div 
              className={`flex-shrink-0 px-4 py-2 rounded-lg border-2 text-sm font-medium cursor-pointer transition-colors ${
                content?.categoryItemSize === 'small' ? 'text-xs px-3 py-1' :
                content?.categoryItemSize === 'large' ? 'text-base px-5 py-3' : 'text-sm px-4 py-2'
              }`}
              style={{ 
                borderColor: content?.categoryBorderColor || '#3B82F6',
                backgroundColor: '#ffffff'
              }}
            >
              Category 1
            </div>
            <div 
              className={`flex-shrink-0 px-4 py-2 rounded-lg border-2 text-sm font-medium cursor-pointer transition-colors ${
                content?.categoryItemSize === 'small' ? 'text-xs px-3 py-1' :
                content?.categoryItemSize === 'large' ? 'text-base px-5 py-3' : 'text-sm px-4 py-2'
              }`}
              style={{ 
                borderColor: content?.categoryBorderColor || '#3B82F6',
                backgroundColor: '#ffffff'
              }}
            >
              Category 2
            </div>
            <div 
              className={`flex-shrink-0 px-4 py-2 rounded-lg border-2 text-sm font-medium cursor-pointer transition-colors ${
                content?.categoryItemSize === 'small' ? 'text-xs px-3 py-1' :
                content?.categoryItemSize === 'large' ? 'text-base px-5 py-3' : 'text-sm px-4 py-2'
              }`}
              style={{ 
                borderColor: content?.categoryBorderColor || '#3B82F6',
                backgroundColor: '#ffffff'
              }}
            >
              Category 3
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">This shows how category navigation will appear with your current settings</p>
        </div>
        
        {/* Category Features Info */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                <Check className="h-3 w-3 text-white" />
              </div>
            </div>
            <div className="text-sm text-emerald-800">
              <p className="font-medium mb-1">Category Navigation Features</p>
              <ul className="space-y-1 text-xs">
                <li>• Horizontal scrollable layout on mobile devices</li>
                <li>• Smooth filtering animation when switching categories</li>
                <li>• Automatic URL updates to maintain category state</li>
                <li>• Keyboard navigation support for accessibility</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    )}
    
    {/* Disabled State Info */}
    {content?.showCategorySlider === false && (
      <div className="border-t border-gray-200 pt-6">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                <Sliders className="h-4 w-4 text-gray-500" />
              </div>
            </div>
            <div>
              <h6 className="font-medium text-gray-700">Category Navigation Disabled</h6>
              <p className="text-sm text-gray-500">All content will be shown without category filtering. Enable navigation above to add category filters.</p>
            </div>
          </div>
        </div>
      </div>
    )}
    
    {/* Configuration Summary */}
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <h6 className="font-medium text-gray-900">Navigation Status</h6>
          <p className="text-sm text-gray-500">Current category navigation configuration</p>
        </div>
        <div className="text-right">
          <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            content?.showCategorySlider !== false 
              ? 'bg-emerald-100 text-emerald-800' 
              : 'bg-gray-100 text-gray-800'
          }`}>
            {content?.showCategorySlider !== false ? 'Enabled' : 'Disabled'}
          </div>
          {content?.showCategorySlider !== false && (
            <div className="text-xs text-gray-500 mt-1 space-y-0.5">
              <div>Size: {(content?.categoryItemSize || 'medium').charAt(0).toUpperCase() + (content?.categoryItemSize || 'medium').slice(1)}</div>
              {content?.initialCategorySlug && (
                <div>Initial: {content.initialCategorySlug}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
</div>
{/* Enhanced Category Display Configuration */}
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-indigo-100 p-2 rounded-lg mr-3">
      <Layout className="h-5 w-5 text-indigo-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Category Display Settings</h4>
      <p className="text-sm text-gray-500 mt-1">Customize how categories are displayed and styled</p>
    </div>
  </div>
  
  <div className="space-y-6">
    {/* Layout Configuration */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Layout & Structure</h5>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Display Mode</label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={content?.categoryDisplayMode || 'slider'}
            onChange={(e) => updateContent('categoryDisplayMode', e.target.value)}
          >
            <option value="slider">Slider (Horizontal Scroll)</option>
            <option value="grid">Grid Layout</option>
            <option value="fullWidth">Full Width (One at a Time)</option>
            <option value="banner">Banner Style</option>
          </select>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              {content?.categoryDisplayMode === 'slider' && 'Categories scroll horizontally, perfect for many categories'}
              {content?.categoryDisplayMode === 'grid' && 'Categories arranged in a responsive grid layout'}
              {content?.categoryDisplayMode === 'fullWidth' && 'One category at a time with navigation arrows'}
              {content?.categoryDisplayMode === 'banner' && 'Wide banner-style category display'}
              {!content?.categoryDisplayMode && 'Categories scroll horizontally, perfect for many categories'}
            </p>
          </div>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Category Shape</label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={content?.categoryShape || 'circle'}
            onChange={(e) => updateContent('categoryShape', e.target.value)}
          >
            <option value="circle">Circle - Classic round shape</option>
            <option value="rectangle">Rectangle - Wide format</option>
            <option value="square">Square - Equal dimensions</option>
            <option value="pill">Pill - Rounded rectangle</option>
          </select>
          <p className="text-xs text-gray-500">Shape of category image containers</p>
        </div>
      </div>
    </div>
    
    {/* Text Configuration */}
    <div className="border-t border-gray-200 pt-6">
      <h5 className="font-medium text-gray-900 mb-4">Text & Labels</h5>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Text Position</label>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          value={content?.categoryTextPosition || 'below'}
          onChange={(e) => updateContent('categoryTextPosition', e.target.value)}
        >
          <option value="below">Below Image - Text under category</option>
          <option value="overlay">Text Overlay - Text on image</option>
          <option value="none">No Text - Images only</option>
        </select>
        <p className="text-xs text-gray-500">Where to place category names relative to images</p>
      </div>
      
      {/* Overlay Settings */}
      {content?.categoryTextPosition === 'overlay' && (
        <div className="mt-6 border-t border-gray-200 pt-6">
          <h6 className="font-medium text-gray-900 mb-4">Text Overlay Settings</h6>
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Overlay Opacity</label>
              <div className="space-y-3">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  value={content?.categoryOverlayOpacity || '0.7'}
                  onChange={(e) => updateContent('categoryOverlayOpacity', e.target.value)}
                />
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-500">Transparent</span>
                  <div className="bg-gray-100 px-2 py-1 rounded text-gray-700 font-medium">
                    {Math.round((content?.categoryOverlayOpacity || 0.7) * 100)}%
                  </div>
                  <span className="text-gray-500">Opaque</span>
                </div>
              </div>
              <p className="text-xs text-gray-500">Transparency level of the overlay background</p>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Text Color</label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                  value={content?.categoryTextColor || '#ffffff'}
                  onChange={(e) => handleColorChange('categoryTextColor', e.target.value)}
                />
                <div 
                  className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                  style={{ backgroundColor: content?.categoryTextColor || '#ffffff' }}
                />
                <span className="text-sm text-gray-600">{content?.categoryTextColor || '#ffffff'}</span>
              </div>
              <p className="text-xs text-gray-500">Color of text when overlaid on category images</p>
            </div>
          </div>
          
          {/* Overlay Preview */}
          <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h6 className="font-medium text-gray-900 mb-3">Overlay Preview</h6>
            <div className="relative w-32 h-32 mx-auto bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg overflow-hidden">
              <div 
                className="absolute inset-0 flex items-center justify-center"
                style={{ 
                  backgroundColor: `rgba(0, 0, 0, ${content?.categoryOverlayOpacity || 0.7})`,
                  color: content?.categoryTextColor || '#ffffff'
                }}
              >
                <span className="text-sm font-medium">Category Name</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">Preview of text overlay with current settings</p>
          </div>
        </div>
      )}
    </div>
    
    {/* Animation Configuration */}
    <div className="border-t border-gray-200 pt-6">
      <h5 className="font-medium text-gray-900 mb-4">Hover Effects</h5>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Hover Animation</label>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          value={content?.categoryHoverEffect || 'zoom'}
          onChange={(e) => updateContent('categoryHoverEffect', e.target.value)}
        >
          <option value="zoom">Zoom - Scale up on hover</option>
          <option value="flip">Flip - 3D flip animation</option>
          <option value="fade">Fade - Opacity transition</option>
          <option value="glow">Glow - Add border glow effect</option>
          <option value="none">None - No hover effect</option>
        </select>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
          <p className="text-xs text-purple-800">
            {content?.categoryHoverEffect === 'zoom' && 'Category will smoothly scale up when users hover over it'}
            {content?.categoryHoverEffect === 'flip' && 'Category will perform a 3D flip animation on hover'}
            {content?.categoryHoverEffect === 'fade' && 'Category will fade in/out with opacity changes'}
            {content?.categoryHoverEffect === 'glow' && 'Category will show a glowing border effect on hover'}
            {content?.categoryHoverEffect === 'none' && 'No animation or visual change on hover'}
            {!content?.categoryHoverEffect && 'Category will smoothly scale up when users hover over it'}
          </p>
        </div>
      </div>
    </div>
    
    {/* Shape Preview */}
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <h6 className="font-medium text-gray-900 mb-3">Category Shape Preview</h6>
      <div className="flex justify-center space-x-4">
        <div className="text-center">
          <div 
            className={`w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 border-2 border-blue-500 flex items-center justify-center text-white text-xs font-medium ${
              content?.categoryShape === 'circle' ? 'rounded-full' :
              content?.categoryShape === 'rectangle' ? 'rounded-lg' :
              content?.categoryShape === 'square' ? 'rounded-lg' :
              content?.categoryShape === 'pill' ? 'rounded-full' : 'rounded-full'
            } ${
              content?.categoryShape === 'rectangle' ? 'w-20 h-12' :
              content?.categoryShape === 'pill' ? 'w-20 h-12' : 'w-16 h-16'
            }`}
          >
            Cat
          </div>
          <p className="text-xs text-gray-500 mt-2 capitalize">
            {content?.categoryShape || 'circle'}
          </p>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-3 text-center">Preview of category shape with current settings</p>
    </div>
    
    {/* Display Features Info */}
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
            <Sparkles className="h-3 w-3 text-white" />
          </div>
        </div>
        <div className="text-sm text-emerald-800">
          <p className="font-medium mb-1">Display Features</p>
          <ul className="space-y-1 text-xs">
            <li>• Responsive design adapts to all screen sizes</li>
            <li>• Smooth animations and transitions</li>
            <li>• Touch-friendly for mobile devices</li>
            <li>• Optimized loading with image lazy-loading</li>
            <li>• Accessibility features for screen readers</li>
          </ul>
        </div>
      </div>
    </div>
    
    {/* Configuration Summary */}
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <h6 className="font-medium text-gray-900">Display Configuration</h6>
          <p className="text-sm text-gray-500">Current category display settings</p>
        </div>
        <div className="text-right text-xs text-gray-500 space-y-0.5">
          <div><span className="font-medium">Mode:</span> {(content?.categoryDisplayMode || 'slider').charAt(0).toUpperCase() + (content?.categoryDisplayMode || 'slider').slice(1)}</div>
          <div><span className="font-medium">Shape:</span> {(content?.categoryShape || 'circle').charAt(0).toUpperCase() + (content?.categoryShape || 'circle').slice(1)}</div>
          <div><span className="font-medium">Text:</span> {(content?.categoryTextPosition || 'below').charAt(0).toUpperCase() + (content?.categoryTextPosition || 'below').slice(1)}</div>
          <div><span className="font-medium">Effect:</span> {(content?.categoryHoverEffect || 'zoom').charAt(0).toUpperCase() + (content?.categoryHoverEffect || 'zoom').slice(1)}</div>
        </div>
      </div>
    </div>
  </div>
</div>

      {/* Enhanced Price Display Configuration */}
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-green-100 p-2 rounded-lg mr-3">
      <DollarSign className="h-5 w-5 text-green-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Price & Cart Configuration</h4>
      <p className="text-sm text-gray-500 mt-1">Configure pricing display and cart functionality</p>
    </div>
  </div>
  
  <div className="space-y-6">
    {/* Price Display Type */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Price Display Settings</h5>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Price Type</label>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          value={content?.priceDisplayType || 'regular'}
          onChange={(e) => updateContent('priceDisplayType', e.target.value)}
        >
          <option value="regular">Regular Price - Standard retail pricing</option>
          <option value="tier">Pricing Tier - Volume-based pricing (last bound)</option>
          <option value="wholesale">Wholesale Price - Bulk pricing for resellers</option>
        </select>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs text-blue-800">
            {content?.priceDisplayType === 'regular' && 'Shows standard retail prices for individual purchases'}
            {content?.priceDisplayType === 'tier' && 'Displays tiered pricing based on quantity breakpoints'}
            {content?.priceDisplayType === 'wholesale' && 'Shows wholesale prices for bulk purchases (typically higher minimum quantities)'}
            {!content?.priceDisplayType && 'Shows standard retail prices for individual purchases'}
          </p>
        </div>
      </div>
    </div>

    {/* Quantity Selection Configuration */}
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center mb-6">
        <div className="bg-purple-100 p-2 rounded-lg mr-3">
          <ShoppingCart className="h-4 w-4 text-purple-600" />
        </div>
        <div>
          <h5 className="font-medium text-gray-900">Cart Functionality</h5>
          <p className="text-sm text-gray-500">Configure how users can add items to their cart</p>
        </div>
      </div>
      
      <div className="space-y-6">
        {/* Enable Quantity Selection */}
        <div className="flex items-start space-x-3">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              id="allowQuantitySelection"
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              checked={content?.allowQuantitySelection === true}
              onChange={(e) => updateContent('allowQuantitySelection', e.target.checked)}
            />
          </div>
          <div className="flex-1">
            <label htmlFor="allowQuantitySelection" className="text-sm font-medium text-gray-700">
              Allow Quantity Selection
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Let users choose how many items to add to cart instead of fixed quantities
            </p>
          </div>
        </div>
        
        {/* Quantity Settings */}
        {content?.allowQuantitySelection && (
          <div className="border-t border-gray-200 pt-6">
            <h6 className="font-medium text-gray-900 mb-4">Quantity Settings</h6>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Default Quantity</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    min="1"
                    value={content?.defaultQuantity || 
                      (content?.priceDisplayType === 'wholesale' ? 50 : 1)}
                    onChange={(e) => updateContent('defaultQuantity', 
                      parseInt(e.target.value, 10) || 
                      (content?.priceDisplayType === 'wholesale' ? 50 : 1)
                    )}
                  />
                  <span className="text-sm text-gray-500">items</span>
                </div>
                <p className="text-xs text-gray-500">Starting quantity when users add items to cart</p>
              </div>
              
              {/* Quantity Rules Information */}
              <div className={`rounded-lg p-4 border ${
                content?.priceDisplayType === 'regular' ? 'bg-blue-50 border-blue-200' :
                content?.priceDisplayType === 'tier' ? 'bg-purple-50 border-purple-200' :
                content?.priceDisplayType === 'wholesale' ? 'bg-orange-50 border-orange-200' :
                'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      content?.priceDisplayType === 'regular' ? 'bg-blue-500' :
                      content?.priceDisplayType === 'tier' ? 'bg-purple-500' :
                      content?.priceDisplayType === 'wholesale' ? 'bg-orange-500' :
                      'bg-blue-500'
                    }`}>
                      <Info className="h-3 w-3 text-white" />
                    </div>
                  </div>
                  <div className={`text-sm ${
                    content?.priceDisplayType === 'regular' ? 'text-blue-800' :
                    content?.priceDisplayType === 'tier' ? 'text-purple-800' :
                    content?.priceDisplayType === 'wholesale' ? 'text-orange-800' :
                    'text-blue-800'
                  }`}>
                    <p className="font-medium mb-1">
                      {content?.priceDisplayType === 'regular' && 'Regular Price Quantity Rules'}
                      {content?.priceDisplayType === 'tier' && 'Tier Pricing Quantity Rules'}
                      {content?.priceDisplayType === 'wholesale' && 'Wholesale Quantity Rules'}
                      {!content?.priceDisplayType && 'Regular Price Quantity Rules'}
                    </p>
                    <p className="text-xs">
                      {content?.priceDisplayType === 'regular' && 
                        "Users can increment quantity by 1. Recommended default: 1 item."}
                      {content?.priceDisplayType === 'tier' && 
                        "Quantity follows tier bounds. Users will see pricing changes at tier breakpoints."}
                      {content?.priceDisplayType === 'wholesale' && 
                        "Higher minimum quantities typical. Recommended default: 50 items."}
                      {!content?.priceDisplayType && 
                        "Users can increment quantity by 1. Recommended default: 1 item."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Quantity Selection Disabled State */}
        {!content?.allowQuantitySelection && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <ShoppingCart className="h-4 w-4 text-gray-500" />
                </div>
              </div>
              <div>
                <h6 className="font-medium text-gray-700">Fixed Quantity Mode</h6>
                <p className="text-sm text-gray-500">Items will be added to cart with predefined quantities. Users cannot adjust quantities during add-to-cart.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    
    {/* Pricing Preview */}
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <h6 className="font-medium text-gray-900 mb-3">Pricing Preview</h6>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-3 h-3 rounded-full ${
            content?.priceDisplayType === 'regular' ? 'bg-blue-500' :
            content?.priceDisplayType === 'tier' ? 'bg-purple-500' :
            content?.priceDisplayType === 'wholesale' ? 'bg-orange-500' :
            'bg-blue-500'
          }`} />
          <span className="text-sm font-medium text-gray-900">
            {content?.priceDisplayType === 'regular' && 'Regular Pricing'}
            {content?.priceDisplayType === 'tier' && 'Tier Pricing'}
            {content?.priceDisplayType === 'wholesale' && 'Wholesale Pricing'}
            {!content?.priceDisplayType && 'Regular Pricing'}
          </span>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium text-gray-900">
            {content?.allowQuantitySelection ? 'Variable Quantity' : 'Fixed Quantity'}
          </div>
          {content?.allowQuantitySelection && (
            <div className="text-xs text-gray-500">
              Default: {content?.defaultQuantity || (content?.priceDisplayType === 'wholesale' ? 50 : 1)} items
            </div>
          )}
        </div>
      </div>
    </div>
    
    {/* Configuration Summary */}
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <h6 className="font-medium text-gray-900">Price Configuration</h6>
          <p className="text-sm text-gray-500">Current pricing and cart settings</p>
        </div>
        <div className="text-right text-xs text-gray-500 space-y-0.5">
          <div>
            <span className="font-medium">Type:</span> {(content?.priceDisplayType || 'regular').charAt(0).toUpperCase() + (content?.priceDisplayType || 'regular').slice(1)}
          </div>
          <div>
            <span className="font-medium">Quantity:</span> {content?.allowQuantitySelection ? 'Selectable' : 'Fixed'}
          </div>
          {content?.allowQuantitySelection && (
            <div>
              <span className="font-medium">Default:</span> {content?.defaultQuantity || (content?.priceDisplayType === 'wholesale' ? 50 : 1)}
            </div>
          )}
        </div>
      </div>
    </div>
    
    {/* Price Features Info */}
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
            <Zap className="h-3 w-3 text-white" />
          </div>
        </div>
        <div className="text-sm text-emerald-800">
          <p className="font-medium mb-1">Pricing Features</p>
          <ul className="space-y-1 text-xs">
            <li>• Real-time price calculations based on quantity</li>
            <li>• Automatic tier pricing updates (when applicable)</li>
            <li>• Mobile-optimized quantity selectors</li>
            <li>• Cart persistence across sessions</li>
            <li>• Integration with inventory management</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</div>

<div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800">Product Display Configuration</h3>
          <p className="text-sm text-gray-600 mt-1">Customize how your products appear to customers</p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          <button
            onClick={() => setActiveSection('display')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeSection === 'display'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                : 'text-gray-500 hover:text-gray-700 hover:bg-white'
            }`}
          >
            Display Fields
          </button>
          <button
            onClick={() => setActiveSection('layout')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeSection === 'layout'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                : 'text-gray-500 hover:text-gray-700 hover:bg-white'
            }`}
          >
            Layout & Style
          </button>
        </div>

        <div className="p-6">
          {/* Display Fields Section */}
          {activeSection === 'display' && (
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-medium text-gray-800 mb-4">Information Fields</h4>
                <p className="text-sm text-gray-600 mb-4">Choose which product information to show to your customers</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'showName', label: 'Product Name', desc: 'Display the product title' },
                    { key: 'showPrice', label: 'Price', desc: 'Show product pricing' },
                    { key: 'showDescription', label: 'Description', desc: 'Include product details' },
                    { key: 'showNote', label: 'Notes', desc: 'Additional product notes' },
                    { key: 'showRating', label: 'Rating', desc: 'Customer review ratings' }
                  ].map(({ key, label, desc }) => (
                    <div 
                      key={key}
                      className={`relative p-4 rounded-lg border-2 transition-all cursor-pointer ${
                        content?.productDisplayFields?.[key] 
                          ? 'border-blue-200 bg-blue-50' 
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      onClick={() => updateContent('productDisplayFields', {
                        ...(content?.productDisplayFields || {}),
                        [key]: !content?.productDisplayFields?.[key]
                      })}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            content?.productDisplayFields?.[key]
                              ? 'bg-blue-600 border-blue-600'
                              : 'border-gray-300'
                          }`}>
                            {content?.productDisplayFields?.[key] && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="text-sm font-medium text-gray-900">{label}</h5>
                          <p className="text-xs text-gray-500 mt-1">{desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Conditional Options */}
              {(content?.productDisplayFields?.showName || content?.productDisplayFields?.showDescription) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-medium text-gray-800 mb-3">Text Display Options</h5>
                  <div className="space-y-4">
                    {content?.productDisplayFields?.showName && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Product Name Lines
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { value: 1, label: '1 Line' },
                            { value: 2, label: '2 Lines' },
                            { value: 3, label: '3 Lines' },
                            { value: 0, label: 'No Limit' }
                          ].map(({ value, label }) => (
                            <button
                              key={value}
                              onClick={() => updateContent('productNameLines', value)}
                              className={`px-3 py-2 text-sm rounded-md transition-colors ${
                                content?.productNameLines === value
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {content?.productDisplayFields?.showDescription && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description Lines
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { value: 1, label: '1 Line' },
                            { value: 2, label: '2 Lines' },
                            { value: 3, label: '3 Lines' },
                            { value: 4, label: '4 Lines' },
                            { value: 5, label: '5 Lines' },
                            { value: 0, label: 'No Limit' }
                          ].map(({ value, label }) => (
                            <button
                              key={value}
                              onClick={() => updateContent('productDescriptionLines', value)}
                              className={`px-3 py-2 text-sm rounded-md transition-colors ${
                                content?.productDescriptionLines === value
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Layout & Style Section */}
          {activeSection === 'layout' && (
            <div className="space-y-6">
              {/* Info Position */}
              <div>
                <h4 className="text-lg font-medium text-gray-800 mb-2">Product Information Position</h4>
                <p className="text-sm text-gray-600 mb-4">Choose where product details appear relative to the image</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { value: 'below', label: 'Below Image', icon: '⬇️' },
                    { value: 'right', label: 'Right of Image', icon: '➡️' },
                    { value: 'left', label: 'Left of Image', icon: '⬅️' },
                    { value: 'overlay', label: 'Overlay on Image', icon: '📋' }
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => updateContent('productInfoPosition', value)}
                      className={`p-4 rounded-lg border-2 text-center transition-all ${
                        content?.productInfoPosition === value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      <div className="text-sm font-medium">{label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Text Alignment */}
              <div>
                <h4 className="text-lg font-medium text-gray-800 mb-2">Text Alignment</h4>
                <p className="text-sm text-gray-600 mb-4">How should the product text be aligned?</p>
                
                <div className="flex gap-3">
                  {[
                    { value: 'left', label: 'Left' },
                    { value: 'center', label: 'Center' },
                    { value: 'right', label: 'Right' }
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => updateContent('productTextAlignment', value)}
                      className={`flex-1 p-3 rounded-lg border-2 text-center transition-all ${
                        content?.productTextAlignment === value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      <div className="text-sm font-medium">{label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Image Aspect Ratio */}
              <div>
                <h4 className="text-lg font-medium text-gray-800 mb-2">Image Aspect Ratio</h4>
                <p className="text-sm text-gray-600 mb-4">Choose how product images should be displayed</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { value: 'square', label: 'Square', desc: '1:1 ratio' },
                    { value: 'portrait', label: 'Portrait', desc: '3:4 ratio' },
                    { value: 'landscape', label: 'Landscape', desc: '4:3 ratio' },
                    { value: 'original', label: 'Original', desc: 'As uploaded' }
                  ].map(({ value, label, desc }) => (
                    <button
                      key={value}
                      onClick={() => updateContent('productImageAspectRatio', value)}
                      className={`p-4 rounded-lg border-2 text-center transition-all ${
                        content?.productImageAspectRatio === value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      <div className="text-sm font-medium mb-1">{label}</div>
                      <div className="text-xs text-gray-500">{desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Preview Section */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h5 className="text-sm font-medium text-gray-800">Configuration Preview</h5>
              <p className="text-xs text-gray-600">Changes will be applied automatically</p>
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span>Auto-saved</span>
            </div>
          </div>
        </div>
      </div>
    </div>


    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800">Product Interaction Configuration</h3>
          <p className="text-sm text-gray-600 mt-1">Control how customers interact with your products</p>
        </div>

        <div className="p-6 space-y-8">
          {/* Click Behavior Section */}
          <div>
            <h4 className="text-lg font-medium text-gray-800 mb-2">Product Click Behavior</h4>
            <p className="text-sm text-gray-600 mb-6">Choose what happens when customers click on a product</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  value: 'navigate',
                  title: 'Navigate to Detail Page',
                  description: 'Take users to a dedicated product page with full details',
                  icon: (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  )
                },
                {
                  value: 'popup',
                  title: 'Show Details in Popup',
                  description: 'Display product information in an overlay without leaving the page',
                  icon: (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  )
                }
              ].map(({ value, title, description, icon }) => (
                <div 
                  key={value}
                  className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all ${
                    content?.productClickBehavior === value
                      ? 'border-purple-300 bg-purple-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                  }`}
                  onClick={() => updateContent('productClickBehavior', value)}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`flex-shrink-0 p-2 rounded-lg ${
                      content?.productClickBehavior === value
                        ? 'bg-purple-100 text-purple-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          content?.productClickBehavior === value
                            ? 'border-purple-600 bg-purple-600'
                            : 'border-gray-300'
                        }`}>
                          {content?.productClickBehavior === value && (
                            <div className="w-2 h-2 rounded-full bg-white"></div>
                          )}
                        </div>
                        <h5 className="text-sm font-semibold text-gray-900">{title}</h5>
                      </div>
                      <p className="text-xs text-gray-600 mt-2 ml-7">{description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Popup Settings Section */}
          {content?.productClickBehavior === 'popup' && (
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                  </svg>
                </div>
                <h4 className="text-lg font-medium text-gray-800">Popup Configuration</h4>
              </div>

              <div className="space-y-8">
                {/* Information Display */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-3">Information to Display</h5>
                  <p className="text-sm text-gray-600 mb-4">Select which product details to show in the popup</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[
                      { key: 'showGallery', label: 'Image Gallery', desc: 'Product photos and images', icon: '🖼️' },
                      { key: 'showDescription', label: 'Description', desc: 'Detailed product information', icon: '📝' },
                      { key: 'showPrice', label: 'Price Information', desc: 'Pricing and cost details', icon: '💰' },
                      { key: 'showNote', label: 'Product Note', desc: 'Additional notes and tips', icon: '📋' },
                      { key: 'showAttributes', label: 'Product Attributes', desc: 'Size, color, specifications', icon: '🏷️' }
                    ].map(({ key, label, desc }) => (
                      <div 
                        key={key}
                        className={`relative p-4 rounded-lg border-2 transition-all cursor-pointer ${
                          content?.productPopupSettings?.[key] 
                            ? 'border-purple-200 bg-purple-50' 
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                        onClick={() => updateContent('productPopupSettings', {
                          ...(content?.productPopupSettings || {}),
                          [key]: !content?.productPopupSettings?.[key]
                        })}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-0.5">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              content?.productPopupSettings?.[key]
                                ? 'bg-purple-600 border-purple-600'
                                : 'border-gray-300'
                            }`}>
                              {content?.productPopupSettings?.[key] && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h6 className="text-sm font-medium text-gray-900">{label}</h6>
                            <p className="text-xs text-gray-500 mt-1">{desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-3">Action Buttons</h5>
                  <p className="text-sm text-gray-600 mb-4">Choose which action buttons to display</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { 
                        key: 'showAddToCart', 
                        label: 'Add to Cart Button', 
                        desc: 'Allow customers to add items to cart',
                        icon: (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m4.5-5a2 2 0 100 4 2 2 0 000-4zm6 0a2 2 0 100 4 2 2 0 000-4z" />
                          </svg>
                        )
                      },
                      { 
                        key: 'showCustomizeButton', 
                        label: 'Customize Product Button', 
                        desc: 'Let customers personalize products',
                        icon: (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                          </svg>
                        )
                      }
                    ].map(({ key, label, desc, icon }) => (
                      <div 
                        key={key}
                        className={`relative p-4 rounded-lg border-2 transition-all cursor-pointer ${
                          content?.productPopupSettings?.[key] 
                            ? 'border-green-200 bg-green-50' 
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                        onClick={() => updateContent('productPopupSettings', {
                          ...(content?.productPopupSettings || {}),
                          [key]: !content?.productPopupSettings?.[key]
                        })}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`flex-shrink-0 p-2 rounded-lg ${
                            content?.productPopupSettings?.[key]
                              ? 'bg-green-100 text-green-600'
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                                content?.productPopupSettings?.[key]
                                  ? 'bg-green-600 border-green-600'
                                  : 'border-gray-300'
                              }`}>
                                {content?.productPopupSettings?.[key] && (
                                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <h6 className="text-sm font-medium text-gray-900">{label}</h6>
                            </div>
                            <p className="text-xs text-gray-500 mt-1 ml-6">{desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Popup Size */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-3">Popup Size</h5>
                  <p className="text-sm text-gray-600 mb-4">Choose how large the popup window should be</p>
                  
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                      { value: 'small', label: 'Small', desc: 'Compact view', width: 'w-16' },
                      { value: 'medium', label: 'Medium', desc: 'Balanced size', width: 'w-20' },
                      { value: 'large', label: 'Large', desc: 'Detailed view', width: 'w-24' },
                      { value: 'fullscreen', label: 'Full Screen', desc: 'Maximum space', width: 'w-28' }
                    ].map(({ value, label, desc, width }) => (
                      <button
                        key={value}
                        onClick={() => updateContent('productPopupSettings', {
                          ...(content?.productPopupSettings || {}),
                          popupSize: value
                        })}
                        className={`p-4 rounded-lg border-2 text-center transition-all ${
                          content?.productPopupSettings?.popupSize === value
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        <div className={`mx-auto mb-2 h-8 bg-gray-300 rounded ${width}`}></div>
                        <div className="text-sm font-medium">{label}</div>
                        <div className="text-xs text-gray-500 mt-1">{desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h6 className="text-sm font-medium text-gray-800">Interaction Settings</h6>
              <p className="text-xs text-gray-600">Configure how customers engage with your products</p>
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span>Live preview ready</span>
            </div>
          </div>
        </div>
      </div>
    </div>
      
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800">Product Grid Configuration</h3>
          <p className="text-sm text-gray-600 mt-1">Customize how your products are displayed in a grid layout</p>
        </div>

        <div className="p-6 space-y-8">
          {/* Products Per Row */}
          <div>
            <h4 className="text-lg font-medium text-gray-800 mb-2">Products Per Row</h4>
            <p className="text-sm text-gray-600 mb-4">Choose how many products to display in each row</p>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { value: 1, label: '1 Product', desc: 'Full width display' },
                { value: 2, label: '2 Products', desc: 'Half width each' },
                { value: 3, label: '3 Products', desc: 'Third width each' },
                { value: 4, label: '4 Products', desc: 'Quarter width each' }
              ].map(({ value, label, desc }) => (
                <button
                  key={value}
                  onClick={() => updateContent('productsPerRow', value)}
                  className={`p-4 rounded-lg border-2 text-center transition-all ${
                    content?.productsPerRow === value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'
                  }`}
                >
                  {/* Visual Grid Preview */}
                  <div className="mb-3 h-12 flex items-center justify-center">
                    <div className="flex space-x-1">
                      {Array.from({ length: value }, (_, i) => (
                        <div 
                          key={i}
                          className={`bg-gray-300 rounded ${
                            value === 1 ? 'w-12 h-8' :
                            value === 2 ? 'w-5 h-6' :
                            value === 3 ? 'w-3 h-5' : 'w-2 h-4'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="text-sm font-medium">{label}</div>
                  <div className="text-xs text-gray-500 mt-1">{desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Product Card Style */}
          <div>
            <h4 className="text-lg font-medium text-gray-800 mb-2">Product Card Style</h4>
            <p className="text-sm text-gray-600 mb-4">Select the visual style for your product cards</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  value: 'shadow',
                  title: 'Shadow',
                  description: 'Cards with subtle drop shadows for depth',
                  preview: (
                    <div className="w-full h-16 bg-white rounded border shadow-md flex items-center justify-center">
                      <div className="w-8 h-8 bg-gray-200 rounded"></div>
                    </div>
                  )
                },
                {
                  value: 'outline',
                  title: 'Outline',
                  description: 'Clean bordered cards with clear boundaries',
                  preview: (
                    <div className="w-full h-16 bg-white rounded border-2 border-gray-300 flex items-center justify-center">
                      <div className="w-8 h-8 bg-gray-200 rounded"></div>
                    </div>
                  )
                },
                {
                  value: 'flat',
                  title: 'Flat',
                  description: 'Minimal style without borders or shadows',
                  preview: (
                    <div className="w-full h-16 bg-gray-50 rounded flex items-center justify-center">
                      <div className="w-8 h-8 bg-gray-200 rounded"></div>
                    </div>
                  )
                }
              ].map(({ value, title, description, preview }) => (
                <div 
                  key={value}
                  className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all ${
                    content?.productCardStyle === value
                      ? 'border-indigo-300 bg-indigo-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                  }`}
                  onClick={() => updateContent('productCardStyle', value)}
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      content?.productCardStyle === value
                        ? 'border-indigo-600 bg-indigo-600'
                        : 'border-gray-300'
                    }`}>
                      {content?.productCardStyle === value && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>
                    <h5 className="text-sm font-semibold text-gray-900">{title}</h5>
                  </div>
                  
                  {/* Style Preview */}
                  <div className="mb-3">
                    {preview}
                  </div>
                  
                  <p className="text-xs text-gray-600">{description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Action Configuration */}
          <div>
            <h4 className="text-lg font-medium text-gray-800 mb-2">Quick Action Button</h4>
            <p className="text-sm text-gray-600 mb-4">Add a quick action button that appears on product hover</p>
            
            {/* Enable/Disable Toggle */}
            <div 
              className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all mb-4 ${
                content?.showQuickAction !== false
                  ? 'border-green-200 bg-green-50' 
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
              onClick={() => updateContent('showQuickAction', !content?.showQuickAction)}
            >
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className={`relative w-12 h-6 rounded-full transition-colors ${
                    content?.showQuickAction !== false ? 'bg-green-500' : 'bg-gray-300'
                  }`}>
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      content?.showQuickAction !== false ? 'translate-x-6' : 'translate-x-0.5'
                    }`}></div>
                  </div>
                </div>
                <div className="flex-1">
                  <h5 className="text-sm font-medium text-gray-900">Show Quick Action Button</h5>
                  <p className="text-xs text-gray-600 mt-1">
                    {content?.showQuickAction !== false 
                      ? 'Quick action button will appear when customers hover over products'
                      : 'No quick action button will be shown'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Action Icon Selection */}
            {content?.showQuickAction !== false && (
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200">
                <h5 className="font-medium text-gray-800 mb-3">Quick Action Icon</h5>
                <p className="text-sm text-gray-600 mb-4">Choose which action the quick button should perform</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    {
                      value: 'customize',
                      title: 'Customize Product',
                      description: 'Allow customers to personalize the product',
                      icon: (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      )
                    },
                    {
                      value: 'cart',
                      title: 'Add to Cart',
                      description: 'Quick add to shopping cart action',
                      icon: (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m4.5-5a2 2 0 100 4 2 2 0 000-4zm6 0a2 2 0 100 4 2 2 0 000-4z" />
                        </svg>
                      )
                    }
                  ].map(({ value, title, description, icon }) => (
                    <div 
                      key={value}
                      className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        content?.quickActionIcon === value
                          ? 'border-blue-300 bg-blue-50 shadow-md'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                      }`}
                      onClick={() => updateContent('quickActionIcon', value)}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`flex-shrink-0 p-2 rounded-lg ${
                          content?.quickActionIcon === value
                            ? 'bg-blue-100 text-blue-600'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              content?.quickActionIcon === value
                                ? 'border-blue-600 bg-blue-600'
                                : 'border-gray-300'
                            }`}>
                              {content?.quickActionIcon === value && (
                                <div className="w-2 h-2 rounded-full bg-white"></div>
                              )}
                            </div>
                            <h6 className="text-sm font-semibold text-gray-900">{title}</h6>
                          </div>
                          <p className="text-xs text-gray-600 mt-2 ml-7">{description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h6 className="text-sm font-medium text-gray-800">Grid Layout Settings</h6>
              <p className="text-xs text-gray-600">Customize your product grid appearance and behavior</p>
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span>Grid preview available</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">API Configuration</h3>
                  <p className="text-sm text-gray-600 mt-1">Advanced settings for API endpoints and data sources</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <button
                onMouseEnter={() => setShowTooltip('info')}
                onMouseLeave={() => setShowTooltip('')}
                className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors"
              >
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              
              {showTooltip === 'info' && (
                <div className="absolute right-0 top-10 w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-10">
                  <h6 className="font-medium text-gray-800 mb-2">Advanced Configuration</h6>
                  <p className="text-sm text-gray-600">
                    These settings control API endpoints for data retrieval. Only modify if you're using custom backend services or have specific integration requirements.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Warning Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1">
                <h5 className="text-sm font-medium text-amber-800 mb-1">Advanced Users Only</h5>
                <p className="text-sm text-amber-700">
                  These settings are for advanced users with custom backend configurations. 
                  Most users should leave these at their default values. Incorrect values may cause the application to stop working.
                </p>
              </div>
            </div>
          </div>

          {/* Advanced Mode Toggle */}
          <div className="mb-6">
            <div 
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setIsAdvancedMode(!isAdvancedMode)}
            >
              <div className="flex items-center space-x-3">
                <div className={`relative w-12 h-6 rounded-full transition-colors ${
                  isAdvancedMode ? 'bg-orange-500' : 'bg-gray-300'
                }`}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    isAdvancedMode ? 'translate-x-6' : 'translate-x-0.5'
                  }`}></div>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-900">Enable Advanced Configuration</h5>
                  <p className="text-xs text-gray-600">
                    {isAdvancedMode ? 'Advanced settings are now editable' : 'Advanced settings are protected'}
                  </p>
                </div>
              </div>
              
              <svg className={`w-5 h-5 text-gray-400 transition-transform ${isAdvancedMode ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          {/* API Configuration Fields */}
          {isAdvancedMode && (
            <div className="space-y-6">
              {/* Category API Path */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Category API Path</label>
                  <button
                    onMouseEnter={() => setShowTooltip('category')}
                    onMouseLeave={() => setShowTooltip('')}
                    className="relative w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors"
                  >
                    <svg className="w-2.5 h-2.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    
                    {showTooltip === 'category' && (
                      <div className="absolute bottom-6 right-0 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-10">
                        <p className="text-xs text-gray-600">
                          The API endpoint used to fetch product categories. This should return a JSON array of category objects.
                        </p>
                      </div>
                    )}
                  </button>
                </div>
                
                <div className="relative">
                  <input
                    type="text"
                    className={`w-full p-3 border rounded-lg font-mono text-sm transition-colors ${
                      !categoryValidation.isValid 
                        ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200' 
                        : 'border-gray-300 bg-white focus:border-blue-500 focus:ring-blue-200'
                    } focus:outline-none focus:ring-2`}
                    value={content?.categoryApiPath || ''}
                    onChange={(e) => updateContent('categoryApiPath', e.target.value)}
                    placeholder="/api/categories"
                  />
                  
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {categoryValidation.isValid ? (
                      <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                </div>
                
                {!categoryValidation.isValid && (
                  <p className="text-xs text-red-600 mt-1 flex items-center space-x-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{categoryValidation.message}</span>
                  </p>
                )}
              </div>

              {/* Product API Path */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Product API Path</label>
                  <button
                    onMouseEnter={() => setShowTooltip('product')}
                    onMouseLeave={() => setShowTooltip('')}
                    className="relative w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors"
                  >
                    <svg className="w-2.5 h-2.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    
                    {showTooltip === 'product' && (
                      <div className="absolute bottom-6 right-0 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-10">
                        <p className="text-xs text-gray-600">
                          The API endpoint used to fetch product data. This should return a JSON array of product objects with basic information.
                        </p>
                      </div>
                    )}
                  </button>
                </div>
                
                <div className="relative">
                  <input
                    type="text"
                    className={`w-full p-3 border rounded-lg font-mono text-sm transition-colors ${
                      !productValidation.isValid 
                        ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200' 
                        : 'border-gray-300 bg-white focus:border-blue-500 focus:ring-blue-200'
                    } focus:outline-none focus:ring-2`}
                    value={content?.productApiPath || ''}
                    onChange={(e) => updateContent('productApiPath', e.target.value)}
                    placeholder="/api/products/basic"
                  />
                  
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {productValidation.isValid ? (
                      <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                </div>
                
                {!productValidation.isValid && (
                  <p className="text-xs text-red-600 mt-1 flex items-center space-x-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{productValidation.message}</span>
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <button
                  onClick={resetToDefaults}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Reset to Defaults
                </button>
                
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <div className={`w-2 h-2 rounded-full ${
                    categoryValidation.isValid && productValidation.isValid ? 'bg-green-400' : 'bg-red-400'
                  }`}></div>
                  <span>
                    {categoryValidation.isValid && productValidation.isValid ? 'Configuration valid' : 'Configuration has errors'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Locked State Message */}
          {!isAdvancedMode && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h5 className="text-lg font-medium text-gray-900 mb-2">Advanced Settings Protected</h5>
              <p className="text-sm text-gray-600 max-w-md mx-auto">
                API configuration is locked to prevent accidental changes. Enable advanced mode above to modify these settings.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h6 className="text-sm font-medium text-gray-800">API Endpoint Configuration</h6>
              <p className="text-xs text-gray-600">Manage data source connections and API paths</p>
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <div className={`w-2 h-2 rounded-full ${isAdvancedMode ? 'bg-orange-400' : 'bg-gray-400'}`}></div>
              <span>{isAdvancedMode ? 'Advanced mode active' : 'Protected mode'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
      

     

   

   
      
      
      
     
    </div>
  );
};

export default ProductCatalogForm;







// // src/components/admin/page-management/section-forms/ProductCatalogForm.js
// import React, { useEffect, useState } from 'react';
// import { Info } from 'lucide-react';
// import axios from 'axios';

// const ProductCatalogForm = ({ content, updateContent }) => {
//   const [availableCategories, setAvailableCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(false);

//   // useEffect to fetch categories
//   useEffect(() => {
//     const fetchCategories = async () => {
//       if (content?.showAllCategories === false) {
//         try {
//           setLoadingCategories(true);
//           const response = await axios.get('/api/categories/basic');
//           setAvailableCategories(response.data);
          
//           // Initialize selected categories if not already set
//           if (!content.selectedCategoryIds) {
//             const initialSelected = response.data.map(cat => cat._id);
//             updateContent('selectedCategoryIds', initialSelected);
//           }
//         } catch (error) {
//           console.error('Error fetching categories:', error);
//         } finally {
//           setLoadingCategories(false);
//         }
//       }
//     };
    
//     fetchCategories();
//   }, [content?.showAllCategories]);

//   // useEffect to update the category selection UI
//   useEffect(() => {
//     const container = document.getElementById('category-selection-container');
//     if (container && availableCategories.length > 0 && content?.showAllCategories === false) {
//       container.innerHTML = '';
      
//       const div = document.createElement('div');
//       div.className = 'max-h-60 overflow-y-auto border rounded p-2';
      
//       availableCategories.forEach(category => {
//         const isSelected = content.selectedCategoryIds?.includes(category._id) || false;
        
//         const checkboxDiv = document.createElement('div');
//         checkboxDiv.className = 'flex items-center mb-2';
        
//         const input = document.createElement('input');
//         input.type = 'checkbox';
//         input.className = 'mr-2';
//         input.checked = isSelected;
//         input.onchange = () => {
//           const currentSelected = [...(content.selectedCategoryIds || [])];
//           if (input.checked && !currentSelected.includes(category._id)) {
//             updateContent('selectedCategoryIds', [...currentSelected, category._id]);
//           } else if (!input.checked && currentSelected.includes(category._id)) {
//             updateContent('selectedCategoryIds', currentSelected.filter(id => id !== category._id));
//           }
//         };
        
//         const label = document.createElement('label');
//         label.className = 'text-sm';
//         label.textContent = category.name;
        
//         checkboxDiv.appendChild(input);
//         checkboxDiv.appendChild(label);
//         div.appendChild(checkboxDiv);
//       });
      
//       container.innerHTML = '';
//       container.appendChild(div);
//     }
//   }, [availableCategories, content?.selectedCategoryIds, content?.showAllCategories]);

//   const handleColorChange = (key, value) => {
//     // Ensure color values include the # if missing
//     if (value && !value.startsWith('#') && value.length > 0) {
//       value = '#' + value;
//     }
//     updateContent(key, value);
//   };
  

//   return (
//     <div>
//       <h3 className="font-bold text-lg mb-4">Product Catalog Section</h3>
      
//       {/* Section Header */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Section Header</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Section Title</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.title || 'Products'}
//             onChange={(e) => updateContent('title', e.target.value)}
//             placeholder="e.g., Our Products, Product Catalog, etc."
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Section Description</label>
//           <textarea
//             className="w-full p-2 border rounded"
//             rows="2"
//             value={content?.description || 'Browse our selection of premium packaging solutions'}
//             onChange={(e) => updateContent('description', e.target.value)}
//             placeholder="Brief description of the product section"
//           ></textarea>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Background Color</label>
//           <input
//             type="color"
//             className="w-full p-1 border rounded h-10"
//             value={content?.backgroundColor || '#F9FAFB'}
//             onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
//           />
//         </div>
//       </div>
      
//       {/* Search Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Search Configuration</h4>
        
//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showSearch"
//             className="mr-2"
//             checked={content?.showSearch !== false}
//             onChange={(e) => updateContent('showSearch', e.target.checked)}
//           />
//           <label htmlFor="showSearch" className="text-sm font-medium">Show Search</label>
//         </div>
        
//         {content?.showSearch !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Search Placeholder</label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded"
//               value={content?.searchPlaceholder || 'Search between all categories...'}
//               onChange={(e) => updateContent('searchPlaceholder', e.target.value)}
//             />
//           </div>
//         )}
//       </div>
      
//       {/* Category Slider Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Category Slider Configuration</h4>
        
//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showCategorySlider"
//             className="mr-2"
//             checked={content?.showCategorySlider !== false}
//             onChange={(e) => updateContent('showCategorySlider', e.target.checked)}
//           />
//           <label htmlFor="showCategorySlider" className="text-sm font-medium">Show Category Slider</label>
//         </div>
        
//         {content?.showCategorySlider !== false && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Category Item Size</label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.categoryItemSize || 'medium'}
//                 onChange={(e) => updateContent('categoryItemSize', e.target.value)}
//               >
//                 <option value="small">Small</option>
//                 <option value="medium">Medium</option>
//                 <option value="large">Large</option>
//               </select>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Category Border Color</label>
//               <input
//                 type="color"
//                 className="w-full p-1 border rounded h-10"
//                 value={content?.categoryBorderColor || '#3B82F6'}
//                 onChange={(e) => handleColorChange('categoryBorderColor', e.target.value)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Selected Category Border Color</label>
//               <input
//                 type="color"
//                 className="w-full p-1 border rounded h-10"
//                 value={content?.selectedCategoryBorderColor || '#F59E0B'}
//                 onChange={(e) => handleColorChange('selectedCategoryBorderColor', e.target.value)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Initial Selected Category Slug (optional)</label>
//               <input
//                 type="text"
//                 className="w-full p-2 border rounded"
//                 value={content?.initialCategorySlug || ''}
//                 onChange={(e) => updateContent('initialCategorySlug', e.target.value)}
//                 placeholder="e.g., shopping-bags"
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 If set, this category will be selected when the page loads. Leave empty to use the URL or no initial selection.
//               </p>
//             </div>
//           </>
//         )}
//       </div>


//       {/* Category Display Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Category Display Configuration</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category Display Mode</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryDisplayMode || 'slider'}
//             onChange={(e) => updateContent('categoryDisplayMode', e.target.value)}
//           >
//             <option value="slider">Slider (Horizontal Scroll)</option>
//             <option value="grid">Grid Layout</option>
//             <option value="fullWidth">Full Width (One at a Time)</option>
//             <option value="banner">Banner Style</option>
//           </select>
//           <p className="text-xs text-gray-500 mt-1">
//             "Full Width" shows one category at a time, with next/prev navigation
//           </p>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category Shape</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryShape || 'circle'}
//             onChange={(e) => updateContent('categoryShape', e.target.value)}
//           >
//             <option value="circle">Circle</option>
//             <option value="rectangle">Rectangle</option>
//             <option value="square">Square</option>
//             <option value="pill">Pill Shape</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category Text Position</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryTextPosition || 'below'}
//             onChange={(e) => updateContent('categoryTextPosition', e.target.value)}
//           >
//             <option value="below">Below Image</option>
//             <option value="overlay">Text Overlay</option>
//             <option value="none">No Text</option>
//           </select>
//         </div>
        
//         {content?.categoryTextPosition === 'overlay' && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Overlay Opacity</label>
//               <input
//                 type="range"
//                 min="0"
//                 max="1"
//                 step="0.1"
//                 className="w-full"
//                 value={content?.categoryOverlayOpacity || '0.7'}
//                 onChange={(e) => updateContent('categoryOverlayOpacity', e.target.value)}
//               />
//               <div className="flex justify-between text-xs">
//                 <span>Transparent</span>
//                 <span>Opaque</span>
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Text Color</label>
//               <input
//                 type="color"
//                 className="w-full p-1 border rounded h-10"
//                 value={content?.categoryTextColor || '#ffffff'}
//                 onChange={(e) => handleColorChange('categoryTextColor', e.target.value)}
//               />
//             </div>
//           </>
//         )}
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Hover Effect</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryHoverEffect || 'zoom'}
//             onChange={(e) => updateContent('categoryHoverEffect', e.target.value)}
//           >
//             <option value="zoom">Zoom</option>
//             <option value="flip">Flip</option>
//             <option value="fade">Fade</option>
//             <option value="glow">Glow</option>
//             <option value="none">None</option>
//           </select>
//         </div>
//       </div>

//       {/* Category Selection */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Category Selection</h4>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               id="showAllCategories"
//               className="mr-2"
//               checked={content?.showAllCategories !== false}
//               onChange={(e) => updateContent('showAllCategories', e.target.checked)}
//             />
//             <label htmlFor="showAllCategories" className="text-sm font-medium">Show All Categories</label>
//           </label>
//         </div>
        
//         {content?.showAllCategories === false && (
//           <div id="category-selection-container" className="mt-4">
//             <label className="block text-sm font-medium mb-2">Select Specific Categories</label>
//             <div className="max-h-60 overflow-y-auto border rounded p-2">
//               <div className="text-sm text-gray-500 mb-2">
//                 Loading categories... If no categories appear, save the page first and edit again.
//               </div>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Price Display Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Price Display Configuration</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Display Price Type</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.priceDisplayType || 'regular'}
//             onChange={(e) => updateContent('priceDisplayType', e.target.value)}
//           >
//             <option value="regular">Regular Price</option>
//             <option value="tier">Pricing Tier (Last Bound)</option>
//             <option value="wholesale">Wholesale Price</option>
//           </select>
//           <p className="text-xs text-gray-500 mt-1">
//             Select which price to display for products when "Show Price" is enabled
//           </p>
//         </div>

//         {/* Quantity Selection for Add to Cart */}
//         <div className="mt-6">
//           <div className="flex items-center mb-4">
//             <input
//               type="checkbox"
//               id="allowQuantitySelection"
//               className="mr-2"
//               checked={content?.allowQuantitySelection === true}
//               onChange={(e) => updateContent('allowQuantitySelection', e.target.checked)}
//             />
//             <label htmlFor="allowQuantitySelection" className="text-sm font-medium">
//               Allow Quantity Selection when Adding to Cart
//             </label>
//           </div>
          
//           {content?.allowQuantitySelection && (
//             <div className="mb-4 ml-6">
//               <label className="block text-sm font-medium mb-1">Default Quantity</label>
//               <input
//                 type="number"
//                 className="w-full p-2 border rounded"
//                 min="1"
//                 value={content?.defaultQuantity || 
//                   (content?.priceDisplayType === 'wholesale' ? 50 : 1)}
//                 onChange={(e) => updateContent('defaultQuantity', 
//                   parseInt(e.target.value, 10) || 
//                   (content?.priceDisplayType === 'wholesale' ? 50 : 1)
//                 )}
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 {content?.priceDisplayType === 'regular' && 
//                   "For regular price, quantity can be incremented by 1 (default is 1)"}
//                 {content?.priceDisplayType === 'tier' && 
//                   "For tier pricing, quantity will follow tier bounds"}
//                 {content?.priceDisplayType === 'wholesale' && 
//                   "For wholesale price, quantity can be incremented by 1 (default is 50)"}
//               </p>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Product Display Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Product Information Display</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-2">Information Fields</label>
//           <div className="space-y-2">
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showName"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showName !== false}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showName: e.target.checked
//                 })}
//               />
//               <label htmlFor="showName" className="text-sm">Show Product Name</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showPrice"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showPrice !== false}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showPrice: e.target.checked
//                 })}
//               />
//               <label htmlFor="showPrice" className="text-sm">Show Price</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showDescription"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showDescription === true}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showDescription: e.target.checked
//                 })}
//               />
//               <label htmlFor="showDescription" className="text-sm">Show Description</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showNote"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showNote === true}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showNote: e.target.checked
//                 })}
//               />
//               <label htmlFor="showNote" className="text-sm">Show Note</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showRating"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showRating === true}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showRating: e.target.checked
//                 })}
//               />
//               <label htmlFor="showRating" className="text-sm">Show Rating</label>
//             </div>
//           </div>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product Info Position</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productInfoPosition || 'below'}
//             onChange={(e) => updateContent('productInfoPosition', e.target.value)}
//           >
//             <option value="below">Below Image</option>
//             <option value="right">Right of Image</option>
//             <option value="left">Left of Image</option>
//             <option value="overlay">Overlay on Image</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Text Alignment</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productTextAlignment || 'left'}
//             onChange={(e) => updateContent('productTextAlignment', e.target.value)}
//           >
//             <option value="left">Left</option>
//             <option value="center">Center</option>
//             <option value="right">Right</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product Name Max Lines</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productNameLines || 2}
//             onChange={(e) => updateContent('productNameLines', parseInt(e.target.value, 10))}
//           >
//             <option value={1}>1 Line</option>
//             <option value={2}>2 Lines</option>
//             <option value={3}>3 Lines</option>
//             <option value={0}>No Limit</option>
//           </select>
//         </div>
        
//         {content?.productDisplayFields?.showDescription && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Description Max Lines</label>
//             <select
//               className="w-full p-2 border rounded"
//               value={content?.productDescriptionLines || 3}
//               onChange={(e) => updateContent('productDescriptionLines', parseInt(e.target.value, 10))}
//             >
//               <option value={1}>1 Line</option>
//               <option value={2}>2 Lines</option>
//               <option value={3}>3 Lines</option>
//               <option value={4}>4 Lines</option>
//               <option value={5}>5 Lines</option>
//               <option value={0}>No Limit</option>
//             </select>
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Image Aspect Ratio</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productImageAspectRatio || 'square'}
//             onChange={(e) => updateContent('productImageAspectRatio', e.target.value)}
//           >
//             <option value="square">Square (1:1)</option>
//             <option value="portrait">Portrait (3:4)</option>
//             <option value="landscape">Landscape (4:3)</option>
//             <option value="original">Original (As uploaded)</option>
//           </select>
//         </div>
//       </div>

//       {/* Product Interaction Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Product Click Behavior</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">When a product is clicked:</label>
//           <div className="space-y-2">
//             <div className="flex items-center">
//               <input
//                 type="radio"
//                 id="productClickNavigate"
//                 name="productClickBehavior"
//                 className="mr-2"
//                 checked={content?.productClickBehavior !== 'popup'}
//                 onChange={() => updateContent('productClickBehavior', 'navigate')}
//               />
//               <label htmlFor="productClickNavigate" className="text-sm">Navigate to product detail page</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="radio"
//                 id="productClickPopup"
//                 name="productClickBehavior"
//                 className="mr-2"
//                 checked={content?.productClickBehavior === 'popup'}
//                 onChange={() => updateContent('productClickBehavior', 'popup')}
//               />
//               <label htmlFor="productClickPopup" className="text-sm">Show product details in popup</label>
//             </div>
//           </div>
//         </div>
        
//         {content?.productClickBehavior === 'popup' && (
//           <div className="border-t pt-4 mt-2">
//             <h5 className="font-medium text-sm mb-3">Popup Settings</h5>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-2">Information to Display</label>
//               <div className="space-y-2">
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showGallery"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showGallery !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showGallery: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showGallery" className="text-sm">Image Gallery</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showDescription"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showDescription !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showDescription: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showDescription" className="text-sm">Description</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showPrice"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showPrice !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showPrice: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showPrice" className="text-sm">Price Information</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showNote"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showNote !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showNote: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showNote" className="text-sm">Product Note</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showAttributes"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showAttributes !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showAttributes: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showAttributes" className="text-sm">Product Attributes</label>
//                 </div>
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-2">Action Buttons</label>
//               <div className="space-y-2">
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showAddToCart"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showAddToCart !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showAddToCart: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showAddToCart" className="text-sm">Add to Cart Button</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showCustomizeButton"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showCustomizeButton !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showCustomizeButton: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showCustomizeButton" className="text-sm">Customize Product Button</label>
//                 </div>
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Popup Size</label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.productPopupSettings?.popupSize || 'medium'}
//                 onChange={(e) => updateContent('productPopupSettings', {
//                   ...(content?.productPopupSettings || {}),
//                   popupSize: e.target.value
//                 })}
//               >
//                 <option value="small">Small</option>
//                 <option value="medium">Medium</option>
//                 <option value="large">Large</option>
//                 <option value="fullscreen">Full Screen</option>
//               </select>
//             </div>
//           </div>
//         )}
//       </div>

      
//       {/* Product Grid Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Product Grid Configuration</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Products Per Row</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productsPerRow || 3}
//             onChange={(e) => updateContent('productsPerRow', parseInt(e.target.value, 10))}
//           >
//             <option value={1}>1 Product</option>
//             <option value={2}>2 Products</option>
//             <option value={3}>3 Products</option>
//             <option value={4}>4 Products</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product Card Style</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productCardStyle || 'shadow'}
//             onChange={(e) => updateContent('productCardStyle', e.target.value)}
//           >
//             <option value="shadow">Shadow</option>
//             <option value="outline">Outline</option>
//             <option value="flat">Flat</option>
//           </select>
//         </div>
        
//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showQuickAction"
//             className="mr-2"
//             checked={content?.showQuickAction !== false}
//             onChange={(e) => updateContent('showQuickAction', e.target.checked)}
//           />
//           <label htmlFor="showQuickAction" className="text-sm font-medium">Show Quick Action Button</label>
//         </div>
        
//         {content?.showQuickAction !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Quick Action Icon</label>
//             <select
//               className="w-full p-2 border rounded"
//               value={content?.quickActionIcon || 'customize'}
//               onChange={(e) => updateContent('quickActionIcon', e.target.value)}
//             >
//               <option value="customize">Customize (Pencil)</option>
//               <option value="cart">Add to Cart</option>
//             </select>
//           </div>
//         )}
//       </div>
      
//       {/* API Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <div className="flex justify-between items-start mb-3">
//           <h4 className="font-medium text-base">API Configuration</h4>
//           <div className="rounded-full bg-blue-100 p-1">
//             <Info size={16} className="text-blue-600" />
//           </div>
//         </div>
        
//         <p className="text-sm text-gray-600 mb-4">
//           These settings are for advanced users. Most users should leave these at their default values.
//         </p>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category API Path</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.categoryApiPath || '/api/categories'}
//             onChange={(e) => updateContent('categoryApiPath', e.target.value)}
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product API Path</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.productApiPath || '/api/products/basic'}
//             onChange={(e) => updateContent('productApiPath', e.target.value)}
//           />
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProductCatalogForm;


















// // in case of slow speed, roll back to this version
// //working before category selection with high speed
// src/components/admin/page-management/section-forms/ProductCatalogForm.js
// import React from 'react';
// import { Info } from 'lucide-react';

// const ProductCatalogForm = ({ content, updateContent }) => {
//   const handleColorChange = (key, value) => {
//     // Ensure color values include the # if missing
//     if (value && !value.startsWith('#') && value.length > 0) {
//       value = '#' + value;
//     }
//     updateContent(key, value);
//   };

//   return (
//     <div>
//       <h3 className="font-bold text-lg mb-4">Product Catalog Section</h3>
      
//       {/* Section Header */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Section Header</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Section Title</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.title || 'Products'}
//             onChange={(e) => updateContent('title', e.target.value)}
//             placeholder="e.g., Our Products, Product Catalog, etc."
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Section Description</label>
//           <textarea
//             className="w-full p-2 border rounded"
//             rows="2"
//             value={content?.description || 'Browse our selection of premium packaging solutions'}
//             onChange={(e) => updateContent('description', e.target.value)}
//             placeholder="Brief description of the product section"
//           ></textarea>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Background Color</label>
//           <input
//             type="color"
//             className="w-full p-1 border rounded h-10"
//             value={content?.backgroundColor || '#F9FAFB'}
//             onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
//           />
//         </div>
//       </div>
      
//       {/* Search Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Search Configuration</h4>
        
//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showSearch"
//             className="mr-2"
//             checked={content?.showSearch !== false}
//             onChange={(e) => updateContent('showSearch', e.target.checked)}
//           />
//           <label htmlFor="showSearch" className="text-sm font-medium">Show Search</label>
//         </div>
        
//         {content?.showSearch !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Search Placeholder</label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded"
//               value={content?.searchPlaceholder || 'Search between all categories...'}
//               onChange={(e) => updateContent('searchPlaceholder', e.target.value)}
//             />
//           </div>
//         )}
//       </div>
      
//       {/* Category Slider Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Category Slider Configuration</h4>
        
//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showCategorySlider"
//             className="mr-2"
//             checked={content?.showCategorySlider !== false}
//             onChange={(e) => updateContent('showCategorySlider', e.target.checked)}
//           />
//           <label htmlFor="showCategorySlider" className="text-sm font-medium">Show Category Slider</label>
//         </div>
        
//         {content?.showCategorySlider !== false && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Category Item Size</label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.categoryItemSize || 'medium'}
//                 onChange={(e) => updateContent('categoryItemSize', e.target.value)}
//               >
//                 <option value="small">Small</option>
//                 <option value="medium">Medium</option>
//                 <option value="large">Large</option>
//               </select>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Category Border Color</label>
//               <input
//                 type="color"
//                 className="w-full p-1 border rounded h-10"
//                 value={content?.categoryBorderColor || '#3B82F6'}
//                 onChange={(e) => handleColorChange('categoryBorderColor', e.target.value)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Selected Category Border Color</label>
//               <input
//                 type="color"
//                 className="w-full p-1 border rounded h-10"
//                 value={content?.selectedCategoryBorderColor || '#F59E0B'}
//                 onChange={(e) => handleColorChange('selectedCategoryBorderColor', e.target.value)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Initial Selected Category Slug (optional)</label>
//               <input
//                 type="text"
//                 className="w-full p-2 border rounded"
//                 value={content?.initialCategorySlug || ''}
//                 onChange={(e) => updateContent('initialCategorySlug', e.target.value)}
//                 placeholder="e.g., shopping-bags"
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 If set, this category will be selected when the page loads. Leave empty to use the URL or no initial selection.
//               </p>
//             </div>
//           </>
//         )}
//       </div>


//       {/* Category Display Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Category Display Configuration</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category Display Mode</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryDisplayMode || 'slider'}
//             onChange={(e) => updateContent('categoryDisplayMode', e.target.value)}
//           >
//             <option value="slider">Slider (Horizontal Scroll)</option>
//             <option value="grid">Grid Layout</option>
//             <option value="fullWidth">Full Width (One at a Time)</option>
//             <option value="banner">Banner Style</option>
//           </select>
//           <p className="text-xs text-gray-500 mt-1">
//             "Full Width" shows one category at a time, with next/prev navigation
//           </p>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category Shape</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryShape || 'circle'}
//             onChange={(e) => updateContent('categoryShape', e.target.value)}
//           >
//             <option value="circle">Circle</option>
//             <option value="rectangle">Rectangle</option>
//             <option value="square">Square</option>
//             <option value="pill">Pill Shape</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category Text Position</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryTextPosition || 'below'}
//             onChange={(e) => updateContent('categoryTextPosition', e.target.value)}
//           >
//             <option value="below">Below Image</option>
//             <option value="overlay">Text Overlay</option>
//             <option value="none">No Text</option>
//           </select>
//         </div>
        
//         {content?.categoryTextPosition === 'overlay' && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Overlay Opacity</label>
//               <input
//                 type="range"
//                 min="0"
//                 max="1"
//                 step="0.1"
//                 className="w-full"
//                 value={content?.categoryOverlayOpacity || '0.7'}
//                 onChange={(e) => updateContent('categoryOverlayOpacity', e.target.value)}
//               />
//               <div className="flex justify-between text-xs">
//                 <span>Transparent</span>
//                 <span>Opaque</span>
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Text Color</label>
//               <input
//                 type="color"
//                 className="w-full p-1 border rounded h-10"
//                 value={content?.categoryTextColor || '#ffffff'}
//                 onChange={(e) => handleColorChange('categoryTextColor', e.target.value)}
//               />
//             </div>
//           </>
//         )}
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Hover Effect</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryHoverEffect || 'zoom'}
//             onChange={(e) => updateContent('categoryHoverEffect', e.target.value)}
//           >
//             <option value="zoom">Zoom</option>
//             <option value="flip">Flip</option>
//             <option value="fade">Fade</option>
//             <option value="glow">Glow</option>
//             <option value="none">None</option>
//           </select>
//         </div>
//       </div>

//       {/* Price Display Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Price Display Configuration</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Display Price Type</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.priceDisplayType || 'regular'}
//             onChange={(e) => updateContent('priceDisplayType', e.target.value)}
//           >
//             <option value="regular">Regular Price</option>
//             <option value="tier">Pricing Tier (Last Bound)</option>
//             <option value="wholesale">Wholesale Price</option>
//           </select>
//           <p className="text-xs text-gray-500 mt-1">
//             Select which price to display for products when "Show Price" is enabled
//           </p>
//         </div>

//         {/* Quantity Selection for Add to Cart */}
//         <div className="mt-6">
//           <div className="flex items-center mb-4">
//             <input
//               type="checkbox"
//               id="allowQuantitySelection"
//               className="mr-2"
//               checked={content?.allowQuantitySelection === true}
//               onChange={(e) => updateContent('allowQuantitySelection', e.target.checked)}
//             />
//             <label htmlFor="allowQuantitySelection" className="text-sm font-medium">
//               Allow Quantity Selection when Adding to Cart
//             </label>
//           </div>
          
//           {content?.allowQuantitySelection && (
//             <div className="mb-4 ml-6">
//               <label className="block text-sm font-medium mb-1">Default Quantity</label>
//               <input
//                 type="number"
//                 className="w-full p-2 border rounded"
//                 min="1"
//                 value={content?.defaultQuantity || 
//                   (content?.priceDisplayType === 'wholesale' ? 50 : 1)}
//                 onChange={(e) => updateContent('defaultQuantity', 
//                   parseInt(e.target.value, 10) || 
//                   (content?.priceDisplayType === 'wholesale' ? 50 : 1)
//                 )}
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 {content?.priceDisplayType === 'regular' && 
//                   "For regular price, quantity can be incremented by 1 (default is 1)"}
//                 {content?.priceDisplayType === 'tier' && 
//                   "For tier pricing, quantity will follow tier bounds"}
//                 {content?.priceDisplayType === 'wholesale' && 
//                   "For wholesale price, quantity can be incremented by 1 (default is 50)"}
//               </p>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Product Display Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Product Information Display</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-2">Information Fields</label>
//           <div className="space-y-2">
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showName"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showName !== false}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showName: e.target.checked
//                 })}
//               />
//               <label htmlFor="showName" className="text-sm">Show Product Name</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showPrice"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showPrice !== false}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showPrice: e.target.checked
//                 })}
//               />
//               <label htmlFor="showPrice" className="text-sm">Show Price</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showDescription"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showDescription === true}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showDescription: e.target.checked
//                 })}
//               />
//               <label htmlFor="showDescription" className="text-sm">Show Description</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showNote"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showNote === true}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showNote: e.target.checked
//                 })}
//               />
//               <label htmlFor="showNote" className="text-sm">Show Note</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showRating"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showRating === true}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showRating: e.target.checked
//                 })}
//               />
//               <label htmlFor="showRating" className="text-sm">Show Rating</label>
//             </div>
//           </div>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product Info Position</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productInfoPosition || 'below'}
//             onChange={(e) => updateContent('productInfoPosition', e.target.value)}
//           >
//             <option value="below">Below Image</option>
//             <option value="right">Right of Image</option>
//             <option value="left">Left of Image</option>
//             <option value="overlay">Overlay on Image</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Text Alignment</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productTextAlignment || 'left'}
//             onChange={(e) => updateContent('productTextAlignment', e.target.value)}
//           >
//             <option value="left">Left</option>
//             <option value="center">Center</option>
//             <option value="right">Right</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product Name Max Lines</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productNameLines || 2}
//             onChange={(e) => updateContent('productNameLines', parseInt(e.target.value, 10))}
//           >
//             <option value={1}>1 Line</option>
//             <option value={2}>2 Lines</option>
//             <option value={3}>3 Lines</option>
//             <option value={0}>No Limit</option>
//           </select>
//         </div>
        
//         {content?.productDisplayFields?.showDescription && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Description Max Lines</label>
//             <select
//               className="w-full p-2 border rounded"
//               value={content?.productDescriptionLines || 3}
//               onChange={(e) => updateContent('productDescriptionLines', parseInt(e.target.value, 10))}
//             >
//               <option value={1}>1 Line</option>
//               <option value={2}>2 Lines</option>
//               <option value={3}>3 Lines</option>
//               <option value={4}>4 Lines</option>
//               <option value={5}>5 Lines</option>
//               <option value={0}>No Limit</option>
//             </select>
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Image Aspect Ratio</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productImageAspectRatio || 'square'}
//             onChange={(e) => updateContent('productImageAspectRatio', e.target.value)}
//           >
//             <option value="square">Square (1:1)</option>
//             <option value="portrait">Portrait (3:4)</option>
//             <option value="landscape">Landscape (4:3)</option>
//             <option value="original">Original (As uploaded)</option>
//           </select>
//         </div>
//       </div>

//       {/* Product Interaction Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Product Click Behavior</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">When a product is clicked:</label>
//           <div className="space-y-2">
//             <div className="flex items-center">
//               <input
//                 type="radio"
//                 id="productClickNavigate"
//                 name="productClickBehavior"
//                 className="mr-2"
//                 checked={content?.productClickBehavior !== 'popup'}
//                 onChange={() => updateContent('productClickBehavior', 'navigate')}
//               />
//               <label htmlFor="productClickNavigate" className="text-sm">Navigate to product detail page</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="radio"
//                 id="productClickPopup"
//                 name="productClickBehavior"
//                 className="mr-2"
//                 checked={content?.productClickBehavior === 'popup'}
//                 onChange={() => updateContent('productClickBehavior', 'popup')}
//               />
//               <label htmlFor="productClickPopup" className="text-sm">Show product details in popup</label>
//             </div>
//           </div>
//         </div>
        
//         {content?.productClickBehavior === 'popup' && (
//           <div className="border-t pt-4 mt-2">
//             <h5 className="font-medium text-sm mb-3">Popup Settings</h5>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-2">Information to Display</label>
//               <div className="space-y-2">
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showGallery"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showGallery !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showGallery: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showGallery" className="text-sm">Image Gallery</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showDescription"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showDescription !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showDescription: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showDescription" className="text-sm">Description</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showPrice"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showPrice !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showPrice: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showPrice" className="text-sm">Price Information</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showNote"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showNote !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showNote: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showNote" className="text-sm">Product Note</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showAttributes"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showAttributes !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showAttributes: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showAttributes" className="text-sm">Product Attributes</label>
//                 </div>
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-2">Action Buttons</label>
//               <div className="space-y-2">
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showAddToCart"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showAddToCart !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showAddToCart: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showAddToCart" className="text-sm">Add to Cart Button</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showCustomizeButton"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showCustomizeButton !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showCustomizeButton: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showCustomizeButton" className="text-sm">Customize Product Button</label>
//                 </div>
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Popup Size</label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.productPopupSettings?.popupSize || 'medium'}
//                 onChange={(e) => updateContent('productPopupSettings', {
//                   ...(content?.productPopupSettings || {}),
//                   popupSize: e.target.value
//                 })}
//               >
//                 <option value="small">Small</option>
//                 <option value="medium">Medium</option>
//                 <option value="large">Large</option>
//                 <option value="fullscreen">Full Screen</option>
//               </select>
//             </div>
//           </div>
//         )}
//       </div>

      
//       {/* Product Grid Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Product Grid Configuration</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Products Per Row</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productsPerRow || 3}
//             onChange={(e) => updateContent('productsPerRow', parseInt(e.target.value, 10))}
//           >
//             <option value={1}>1 Product</option>
//             <option value={2}>2 Products</option>
//             <option value={3}>3 Products</option>
//             <option value={4}>4 Products</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product Card Style</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productCardStyle || 'shadow'}
//             onChange={(e) => updateContent('productCardStyle', e.target.value)}
//           >
//             <option value="shadow">Shadow</option>
//             <option value="outline">Outline</option>
//             <option value="flat">Flat</option>
//           </select>
//         </div>
        
//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showQuickAction"
//             className="mr-2"
//             checked={content?.showQuickAction !== false}
//             onChange={(e) => updateContent('showQuickAction', e.target.checked)}
//           />
//           <label htmlFor="showQuickAction" className="text-sm font-medium">Show Quick Action Button</label>
//         </div>
        
//         {content?.showQuickAction !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Quick Action Icon</label>
//             <select
//               className="w-full p-2 border rounded"
//               value={content?.quickActionIcon || 'customize'}
//               onChange={(e) => updateContent('quickActionIcon', e.target.value)}
//             >
//               <option value="customize">Customize (Pencil)</option>
//               <option value="cart">Add to Cart</option>
//             </select>
//           </div>
//         )}
//       </div>
      
//       {/* API Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <div className="flex justify-between items-start mb-3">
//           <h4 className="font-medium text-base">API Configuration</h4>
//           <div className="rounded-full bg-blue-100 p-1">
//             <Info size={16} className="text-blue-600" />
//           </div>
//         </div>
        
//         <p className="text-sm text-gray-600 mb-4">
//           These settings are for advanced users. Most users should leave these at their default values.
//         </p>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category API Path</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.categoryApiPath || '/api/categories'}
//             onChange={(e) => updateContent('categoryApiPath', e.target.value)}
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product API Path</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.productApiPath || '/api/products/basic'}
//             onChange={(e) => updateContent('productApiPath', e.target.value)}
//           />
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProductCatalogForm;










// // do not remove this version
// // working with price tiering
// // src/components/admin/page-management/section-forms/ProductCatalogForm.js
// import React from 'react';
// import { Info } from 'lucide-react';

// const ProductCatalogForm = ({ content, updateContent }) => {
//   const handleColorChange = (key, value) => {
//     // Ensure color values include the # if missing
//     if (value && !value.startsWith('#') && value.length > 0) {
//       value = '#' + value;
//     }
//     updateContent(key, value);
//   };

//   return (
//     <div>
//       <h3 className="font-bold text-lg mb-4">Product Catalog Section</h3>
      
//       {/* Section Header */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Section Header</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Section Title</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.title || 'Products'}
//             onChange={(e) => updateContent('title', e.target.value)}
//             placeholder="e.g., Our Products, Product Catalog, etc."
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Section Description</label>
//           <textarea
//             className="w-full p-2 border rounded"
//             rows="2"
//             value={content?.description || 'Browse our selection of premium packaging solutions'}
//             onChange={(e) => updateContent('description', e.target.value)}
//             placeholder="Brief description of the product section"
//           ></textarea>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Background Color</label>
//           <input
//             type="color"
//             className="w-full p-1 border rounded h-10"
//             value={content?.backgroundColor || '#F9FAFB'}
//             onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
//           />
//         </div>
//       </div>
      
//       {/* Search Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Search Configuration</h4>
        
//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showSearch"
//             className="mr-2"
//             checked={content?.showSearch !== false}
//             onChange={(e) => updateContent('showSearch', e.target.checked)}
//           />
//           <label htmlFor="showSearch" className="text-sm font-medium">Show Search</label>
//         </div>
        
//         {content?.showSearch !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Search Placeholder</label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded"
//               value={content?.searchPlaceholder || 'Search between all categories...'}
//               onChange={(e) => updateContent('searchPlaceholder', e.target.value)}
//             />
//           </div>
//         )}
//       </div>
      
//       {/* Category Slider Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Category Slider Configuration</h4>
        
//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showCategorySlider"
//             className="mr-2"
//             checked={content?.showCategorySlider !== false}
//             onChange={(e) => updateContent('showCategorySlider', e.target.checked)}
//           />
//           <label htmlFor="showCategorySlider" className="text-sm font-medium">Show Category Slider</label>
//         </div>
        
//         {content?.showCategorySlider !== false && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Category Item Size</label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.categoryItemSize || 'medium'}
//                 onChange={(e) => updateContent('categoryItemSize', e.target.value)}
//               >
//                 <option value="small">Small</option>
//                 <option value="medium">Medium</option>
//                 <option value="large">Large</option>
//               </select>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Category Border Color</label>
//               <input
//                 type="color"
//                 className="w-full p-1 border rounded h-10"
//                 value={content?.categoryBorderColor || '#3B82F6'}
//                 onChange={(e) => handleColorChange('categoryBorderColor', e.target.value)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Selected Category Border Color</label>
//               <input
//                 type="color"
//                 className="w-full p-1 border rounded h-10"
//                 value={content?.selectedCategoryBorderColor || '#F59E0B'}
//                 onChange={(e) => handleColorChange('selectedCategoryBorderColor', e.target.value)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Initial Selected Category Slug (optional)</label>
//               <input
//                 type="text"
//                 className="w-full p-2 border rounded"
//                 value={content?.initialCategorySlug || ''}
//                 onChange={(e) => updateContent('initialCategorySlug', e.target.value)}
//                 placeholder="e.g., shopping-bags"
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 If set, this category will be selected when the page loads. Leave empty to use the URL or no initial selection.
//               </p>
//             </div>
//           </>
//         )}
//       </div>


//       {/* Category Display Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Category Display Configuration</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category Display Mode</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryDisplayMode || 'slider'}
//             onChange={(e) => updateContent('categoryDisplayMode', e.target.value)}
//           >
//             <option value="slider">Slider (Horizontal Scroll)</option>
//             <option value="grid">Grid Layout</option>
//             <option value="fullWidth">Full Width (One at a Time)</option>
//             <option value="banner">Banner Style</option>
//           </select>
//           <p className="text-xs text-gray-500 mt-1">
//             "Full Width" shows one category at a time, with next/prev navigation
//           </p>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category Shape</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryShape || 'circle'}
//             onChange={(e) => updateContent('categoryShape', e.target.value)}
//           >
//             <option value="circle">Circle</option>
//             <option value="rectangle">Rectangle</option>
//             <option value="square">Square</option>
//             <option value="pill">Pill Shape</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category Text Position</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryTextPosition || 'below'}
//             onChange={(e) => updateContent('categoryTextPosition', e.target.value)}
//           >
//             <option value="below">Below Image</option>
//             <option value="overlay">Text Overlay</option>
//             <option value="none">No Text</option>
//           </select>
//         </div>
        
//         {content?.categoryTextPosition === 'overlay' && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Overlay Opacity</label>
//               <input
//                 type="range"
//                 min="0"
//                 max="1"
//                 step="0.1"
//                 className="w-full"
//                 value={content?.categoryOverlayOpacity || '0.7'}
//                 onChange={(e) => updateContent('categoryOverlayOpacity', e.target.value)}
//               />
//               <div className="flex justify-between text-xs">
//                 <span>Transparent</span>
//                 <span>Opaque</span>
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Text Color</label>
//               <input
//                 type="color"
//                 className="w-full p-1 border rounded h-10"
//                 value={content?.categoryTextColor || '#ffffff'}
//                 onChange={(e) => handleColorChange('categoryTextColor', e.target.value)}
//               />
//             </div>
//           </>
//         )}
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Hover Effect</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryHoverEffect || 'zoom'}
//             onChange={(e) => updateContent('categoryHoverEffect', e.target.value)}
//           >
//             <option value="zoom">Zoom</option>
//             <option value="flip">Flip</option>
//             <option value="fade">Fade</option>
//             <option value="glow">Glow</option>
//             <option value="none">None</option>
//           </select>
//         </div>
//       </div>

//       {/* Product Display Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Product Information Display</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-2">Information Fields</label>
//           <div className="space-y-2">
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showName"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showName !== false}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showName: e.target.checked
//                 })}
//               />
//               <label htmlFor="showName" className="text-sm">Show Product Name</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showPrice"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showPrice !== false}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showPrice: e.target.checked
//                 })}
//               />
//               <label htmlFor="showPrice" className="text-sm">Show Price</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showDescription"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showDescription === true}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showDescription: e.target.checked
//                 })}
//               />
//               <label htmlFor="showDescription" className="text-sm">Show Description</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showNote"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showNote === true}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showNote: e.target.checked
//                 })}
//               />
//               <label htmlFor="showNote" className="text-sm">Show Note</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showRating"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showRating === true}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showRating: e.target.checked
//                 })}
//               />
//               <label htmlFor="showRating" className="text-sm">Show Rating</label>
//             </div>
//           </div>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product Info Position</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productInfoPosition || 'below'}
//             onChange={(e) => updateContent('productInfoPosition', e.target.value)}
//           >
//             <option value="below">Below Image</option>
//             <option value="right">Right of Image</option>
//             <option value="left">Left of Image</option>
//             <option value="overlay">Overlay on Image</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Text Alignment</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productTextAlignment || 'left'}
//             onChange={(e) => updateContent('productTextAlignment', e.target.value)}
//           >
//             <option value="left">Left</option>
//             <option value="center">Center</option>
//             <option value="right">Right</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product Name Max Lines</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productNameLines || 2}
//             onChange={(e) => updateContent('productNameLines', parseInt(e.target.value, 10))}
//           >
//             <option value={1}>1 Line</option>
//             <option value={2}>2 Lines</option>
//             <option value={3}>3 Lines</option>
//             <option value={0}>No Limit</option>
//           </select>
//         </div>
        
//         {content?.productDisplayFields?.showDescription && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Description Max Lines</label>
//             <select
//               className="w-full p-2 border rounded"
//               value={content?.productDescriptionLines || 3}
//               onChange={(e) => updateContent('productDescriptionLines', parseInt(e.target.value, 10))}
//             >
//               <option value={1}>1 Line</option>
//               <option value={2}>2 Lines</option>
//               <option value={3}>3 Lines</option>
//               <option value={4}>4 Lines</option>
//               <option value={5}>5 Lines</option>
//               <option value={0}>No Limit</option>
//             </select>
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Image Aspect Ratio</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productImageAspectRatio || 'square'}
//             onChange={(e) => updateContent('productImageAspectRatio', e.target.value)}
//           >
//             <option value="square">Square (1:1)</option>
//             <option value="portrait">Portrait (3:4)</option>
//             <option value="landscape">Landscape (4:3)</option>
//             <option value="original">Original (As uploaded)</option>
//           </select>
//         </div>
//       </div>

//       {/* Product Interaction Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Product Click Behavior</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">When a product is clicked:</label>
//           <div className="space-y-2">
//             <div className="flex items-center">
//               <input
//                 type="radio"
//                 id="productClickNavigate"
//                 name="productClickBehavior"
//                 className="mr-2"
//                 checked={content?.productClickBehavior !== 'popup'}
//                 onChange={() => updateContent('productClickBehavior', 'navigate')}
//               />
//               <label htmlFor="productClickNavigate" className="text-sm">Navigate to product detail page</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="radio"
//                 id="productClickPopup"
//                 name="productClickBehavior"
//                 className="mr-2"
//                 checked={content?.productClickBehavior === 'popup'}
//                 onChange={() => updateContent('productClickBehavior', 'popup')}
//               />
//               <label htmlFor="productClickPopup" className="text-sm">Show product details in popup</label>
//             </div>
//           </div>
//         </div>
        
//         {content?.productClickBehavior === 'popup' && (
//           <div className="border-t pt-4 mt-2">
//             <h5 className="font-medium text-sm mb-3">Popup Settings</h5>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-2">Information to Display</label>
//               <div className="space-y-2">
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showGallery"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showGallery !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showGallery: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showGallery" className="text-sm">Image Gallery</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showDescription"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showDescription !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showDescription: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showDescription" className="text-sm">Description</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showPrice"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showPrice !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showPrice: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showPrice" className="text-sm">Price Information</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showNote"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showNote !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showNote: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showNote" className="text-sm">Product Note</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showAttributes"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showAttributes !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showAttributes: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showAttributes" className="text-sm">Product Attributes</label>
//                 </div>
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-2">Action Buttons</label>
//               <div className="space-y-2">
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showAddToCart"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showAddToCart !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showAddToCart: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showAddToCart" className="text-sm">Add to Cart Button</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showCustomizeButton"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showCustomizeButton !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showCustomizeButton: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showCustomizeButton" className="text-sm">Customize Product Button</label>
//                 </div>
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Popup Size</label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.productPopupSettings?.popupSize || 'medium'}
//                 onChange={(e) => updateContent('productPopupSettings', {
//                   ...(content?.productPopupSettings || {}),
//                   popupSize: e.target.value
//                 })}
//               >
//                 <option value="small">Small</option>
//                 <option value="medium">Medium</option>
//                 <option value="large">Large</option>
//                 <option value="fullscreen">Full Screen</option>
//               </select>
//             </div>
//           </div>
//         )}
//       </div>

      
//       {/* Product Grid Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Product Grid Configuration</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Products Per Row</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productsPerRow || 3}
//             onChange={(e) => updateContent('productsPerRow', parseInt(e.target.value, 10))}
//           >
//             <option value={1}>1 Product</option>
//             <option value={2}>2 Products</option>
//             <option value={3}>3 Products</option>
//             <option value={4}>4 Products</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product Card Style</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productCardStyle || 'shadow'}
//             onChange={(e) => updateContent('productCardStyle', e.target.value)}
//           >
//             <option value="shadow">Shadow</option>
//             <option value="outline">Outline</option>
//             <option value="flat">Flat</option>
//           </select>
//         </div>
        
//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showQuickAction"
//             className="mr-2"
//             checked={content?.showQuickAction !== false}
//             onChange={(e) => updateContent('showQuickAction', e.target.checked)}
//           />
//           <label htmlFor="showQuickAction" className="text-sm font-medium">Show Quick Action Button</label>
//         </div>
        
//         {content?.showQuickAction !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Quick Action Icon</label>
//             <select
//               className="w-full p-2 border rounded"
//               value={content?.quickActionIcon || 'customize'}
//               onChange={(e) => updateContent('quickActionIcon', e.target.value)}
//             >
//               <option value="customize">Customize (Pencil)</option>
//               <option value="cart">Add to Cart</option>
//             </select>
//           </div>
//         )}
//       </div>
      
//       {/* API Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <div className="flex justify-between items-start mb-3">
//           <h4 className="font-medium text-base">API Configuration</h4>
//           <div className="rounded-full bg-blue-100 p-1">
//             <Info size={16} className="text-blue-600" />
//           </div>
//         </div>
        
//         <p className="text-sm text-gray-600 mb-4">
//           These settings are for advanced users. Most users should leave these at their default values.
//         </p>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category API Path</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.categoryApiPath || '/api/categories'}
//             onChange={(e) => updateContent('categoryApiPath', e.target.value)}
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product API Path</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.productApiPath || '/api/products/basic'}
//             onChange={(e) => updateContent('productApiPath', e.target.value)}
//           />
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProductCatalogForm;